import React from 'react';
import { ServiceMockLoggingConfig } from '../../../../../../types/rest/mockServiceTypes';
import { Col, Form, Row } from 'react-bootstrap';
import Switcher from '../../../../../../components/ui/switcher/Switcher';
import { isBase64 } from '../../../../../../utils/miscUtil';
import _ from 'lodash';
import { isFeatureEnabled } from '../../../../../../utils/bzmUserConfigUtil';

interface Props {
  disabled: boolean;
  loggingConfig?: ServiceMockLoggingConfig;
  onUpdate: (loggingConfig: ServiceMockLoggingConfig, debounce?: boolean) => void;
}

const AdvancedLogging = ({ onUpdate, loggingConfig, disabled }: Props) => {
  const onChangeHandler = (name: keyof ServiceMockLoggingConfig, value: any, debounce?: boolean) => {
    if (!loggingConfig) {
      loggingConfig = {
        advancedFileLoggingEnabled: false,
        overwriteExistingFiles: false,
        requestFileName: '',
        responseFileName: '',
        containerFileLoggingEnabled: false,
        eventsFileLoggingEnabled: false,
        sslLoggingEnabled: false,
      };
    }

    //Base encode the value
    if (name === 'requestFileName' || name === 'responseFileName') {
      try {
        value = btoa(value as string);
      } catch (e) {
        console.log('Error encoding value', e);
      }
    }

    const newLoggingConfig = { ...loggingConfig, [name]: value };
    onUpdate(newLoggingConfig, debounce);
  };

  const isAdvancedFileLoggingActive = loggingConfig?.advancedFileLoggingEnabled;

  const getEncodedValue = (attr: 'requestFileName' | 'responseFileName') => {
    const val = loggingConfig?.[attr];
    if (!_.isNil(val) && !_.isEmpty(val) && isBase64(val)) {
      return atob(loggingConfig?.[attr]);
    }
    return loggingConfig?.[attr];
  };

  const createSwitcher = (key: keyof ServiceMockLoggingConfig, label: string, disabled: boolean) => {
    return (
      <Switcher
        key={key}
        className={'mb-4'}
        disabled={disabled}
        label={label}
        size={'md'}
        onChange={() => {
          const newValue = !loggingConfig?.[key];
          onChangeHandler(key, newValue, false);
        }}
        checked={loggingConfig?.[key] as boolean}
      />
    );
  };

  const getCommonLoggingOptions = () => {
    return (
      <div>
        <h4 className={'mb-4'}>Logger Settings</h4>
        {createSwitcher('containerFileLoggingEnabled', 'Container File Logging Enabled', disabled)}
        {createSwitcher('eventsFileLoggingEnabled', 'Events File Logging Enabled', disabled)}
        {createSwitcher('sslLoggingEnabled', 'SSL Logging Enabled', disabled)}
      </div>
    );
  };

  const getAdvancedLoggingOptions = () => {
    return (
      <div>
        <h4 className={'mb-4 mt-6'}>File Logging Settings</h4>

        {createSwitcher('advancedFileLoggingEnabled', 'File Logging Enabled', disabled)}
        {createSwitcher('overwriteExistingFiles', 'Overwrite existing files', disabled || !isAdvancedFileLoggingActive)}

        <div className={'mb-3'}>
          <label>Request Log filename pattern</label>
          <Form.Control
            onChange={event => onChangeHandler('requestFileName', event.target.value, true)}
            disabled={disabled || !isAdvancedFileLoggingActive}
            as={'textarea'}
            rows={3}
            placeholder={'Enter request log filename pattern...'}
            defaultValue={getEncodedValue('requestFileName')}
          />
        </div>

        <div className={'mb-3'}>
          <label>Response Log filename pattern</label>
          <Form.Control
            onChange={event => onChangeHandler('responseFileName', event.target.value, true)}
            disabled={disabled || !isAdvancedFileLoggingActive}
            as={'textarea'}
            rows={3}
            placeholder={'Enter response log filename pattern...'}
            defaultValue={getEncodedValue('responseFileName')}
          />
        </div>
      </div>
    );
  };

  return (
    <Row>
      <Col md={6}>
        {getCommonLoggingOptions()}
        {isFeatureEnabled('enableMockServicesFileLogging') && getAdvancedLoggingOptions()}
      </Col>
    </Row>
  );
};

export default AdvancedLogging;
